import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { IconName } from '@zarya/theme';
import type { RailGroup } from '@zarya/ui';
import type { Shell } from '@app/api/hooks/shell';
import type { Role } from '@app/auth/sessionContext';
import { hasRole } from '@app/auth/useCurrentUser';
import { hasKey, t } from '@app/i18n';

/**
 * Mapa de rotas do console (T2): rótulo, ícone, grupo do menu, papéis e contador.
 *
 * É a fonte única de `src/routes.tsx` (rotas + título de cada tela) e do menu
 * lateral. Vive fora de `routes.tsx` porque aquele arquivo só exporta componentes
 * (`react-refresh/only-export-components`).
 */

export type Counters = Shell['counters'];

/**
 * Grupos do menu, na ordem de FR-012.
 *
 * A constante guarda a CHAVE de catálogo, não o rótulo: o texto sai de
 * `shell.nav.group.*` em tempo de desenho (ver `railGroups`). Ela continua sendo a
 * ordem — que é estrutura, não texto, e por isso não vai para o catálogo.
 */
export const GROUPS = ['operation', 'discovery', 'control', 'settings'] as const;
export type GroupMenu = (typeof GROUPS)[number];

/**
 * A parte do catálogo que descreve o menu (T2).
 *
 * `navLabel`/`screenTitle` resolvem a chave **em tempo de desenho**, e isso não é
 * detalhe de estilo: `ROUTES` é uma const de módulo, avaliada uma vez no import.
 * Um `t(...)` escrito ali congelaria o idioma da primeira renderização e o seletor
 * de idioma trocaria tudo menos o menu — o defeito mais visível possível, e o mais
 * fácil de introduzir sem perceber.
 */
export function navLabel(rota: RouteConsole): string {
  return t(`shell.nav.${rota.key}`);
}

/**
 * `<h1>` da tela.
 *
 * Hoje toda tela repete o rótulo do menu, e por isso a chave é UMA
 * (`shell.nav.<key>`): duplicar quinze frases idênticas em dois locales seria
 * criar trinta lugares para uma divergir em silêncio. Quando um `<h1>` precisar
 * diferir do menu, basta escrever `shell.title.<key>` no catálogo — esta função a
 * prefere assim que ela existir, sem tocar em `ROUTES`.
 */
export function screenTitle(rota: RouteConsole): string {
  const own = `shell.title.${rota.key}`;
  return hasKey(own) ? t(own) : navLabel(rota);
}

export interface RouteConsole {
  href: string;
  /**
   * Chave do catálogo desta rota (`shell.nav.<key>`, e opcionalmente
   * `shell.title.<key>`). É identificador, não texto: não se traduz e não muda
   * quando o rótulo muda.
   */
  key: string;
  icon: IconName;
  group: GroupMenu;
  /** Tela T\* do spec que implementa a rota. */
  tela: string;
  /** Papéis que veem o item. Ausente = todos (FR-009). */
  papeis?: readonly Role[];
  /**
   * `href` do item-pai no rail — a rota aparece recuada sob ele (Fase 2 do
   * «Acesso: pessoa × agente», §2 e §3). Hoje só as duas categorias e Security
   * Contracts o têm; ausente = item de topo do grupo, como sempre foi.
   *
   * É a hierarquia **do menu**, não do endereço: `/contratos` mora sob «Access
   * Control» no rail sem que a rota mude de lugar.
   */
  parent?: string;
  /**
   * `true` = o item é só o rótulo de um grupo de telas no rail: com o ícone e os filhos recuados embaixo, mas sem
   * link, porque não há tela própria (12/10/2026, a tela do «Controle de acesso» saiu). A rota continua na lista
   * porque o endereço continua valendo: ele redireciona para uma das telas de baixo (`routes.tsx`).
   */
  heading?: boolean;
  /** Contador de `getShell` exibido no item (FR-012). */
  contador?: keyof Counters;
  /** Contador sem urgência (verde). Só Aprovações pede atenção (laranja). */
  contadorDiscreto?: boolean;
}

/** Rota da tela de login (T1) — fora do shell e fora do guard de sessão. */
export const ROUTE_LOGIN = '/login';

/**
 * Comandos de laboratório (T24, 28/08) — dentro do shell, **fora de `ROTAS`**.
 *
 * Ficar fora da lista é o que a torna oculta: o rail (`railGroups`) e o mapa de
 * títulos saem daqui, e a busca global é do servidor (identidades, resources e
 * políticas — nunca rotas). Só admin, e a URL é o único caminho.
 */
export const ROUTE_COMMANDS = '/comandos';

/**
 * Console de demonstração (`/console-de-demo`, 29/08) — **fora do shell** e fora
 * de `ROTAS`.
 *
 * A tela T25 («Conversar com agente») saiu do menu Operação a pedido do lead: ela
 * não é funcionalidade do 360 Agentic Identity, é ferramenta de bastidor — fala
 * com os agentes no cluster do laboratório para apoiar a demonstração. Ficar fora
 * de `ROTAS` tira-a do rail e da busca; ficar fora da rota-mãe do `Shell` tira o
 * rail, a topbar, os contadores e o sino, e a página passa a desenhar a si mesma,
 * com identidade visual própria (fundo escuro fixo, que não segue o tema do
 * produto). Só admin, como `/comandos`, e a URL é o único caminho.
 */
export const ROUTE_DEMO = '/console-de-demo';

/**
 * Chat «Ask» (`/chat`, 04/09) — **fora do shell** e fora de `ROTAS`, como o
 * Console de demonstração.
 *
 * É a tela que o cliente vê na demo de 09/09: a pessoa pergunta em linguagem
 * natural, o generalista escolhe a quem delegar, o especialista escolhe a tool
 * — e STS, gateway e PDP ficam no caminho, inteiros. Fora de `ROTAS` para não
 * entrar no rail nem na busca; fora do `Shell` para desenhar a própria moldura.
 * Ao contrário do console de demo, **segue o tema do produto** (claro e escuro):
 * ela é a cara do produto conversando, não bastidor. Papéis admin e approver —
 * os mesmos das rotas `agentes` que ela chama.
 */
export const ROUTE_CHAT = '/chat';

/**
 * O endereço que T25 teve até 29/08. Continua registrado, redirecionando para
 * `ROTA_DEMO`: quem guardou o link (ou o abriu no roteiro da demonstração) chega
 * à página nova em vez de cair na Visão geral pelo `path="*"`.
 */
export const ROUTE_STALE_CONVERSE = '/conversar';

/**
 * Documentação da API (`/docs/api`, 30/08) — **fora do shell** e fora de `ROTAS`, mas
 * atrás do `RequireAuth` desde 30/09 (toda URL da documentação exige login; o nginx exige a
 * mesma sessão nos arquivos estáticos). Ao contrário de `/comandos` e `/console-de-demo`,
 * que também vivem fora de `ROTAS`, esta não é oculta — só não tem item de menu, porque o
 * leitor não é o operador do console.
 *
 * O conteúdo é o recorte público do contrato (`/docs/api-publica.yaml`, gerado
 * por `pnpm api:publica`), renderizado pelo Scalar.
 */
export const ROUTE_DOCSAPI = '/docs/api';

/**
 * Documentação do produto (`/docs`, 16/09) — a mesma moldura de `ROUTE_DOCSAPI`: fora do
 * shell e fora de `ROTAS`, atrás do `RequireAuth` desde 30/09.
 *
 * `/docs` abre a primeira página do sumário; cada página tem endereço próprio em
 * `/docs/<trilha>/<pagina>`. As duas rotas convivem com `/docs/api` sem ambiguidade porque
 * ela tem dois segmentos e a de página tem três — e, no ranking do router, segmento
 * estático ganha de dinâmico de qualquer forma.
 *
 * O conteúdo é o Markdown de `docs/platform/`, servido estático (ver
 * `control-plane/frontend/scripts/sync-docs-platform.mjs`), lido com o cookie da sessão.
 */
export const ROUTE_DOCS = '/docs';

/** Toda rota da documentação — a API e o produto. Quem pergunta é o título da aba. */
export function isDocsRoute(pathname: string): boolean {
  return pathname === ROUTE_DOCS || pathname.startsWith(`${ROUTE_DOCS}/`);
}

/**
 * Human Access (T11) e Identity Access (T14) — os dois eixos da decisão, cada um
 * com a sua categoria no grupo Controle (spec «Acesso: pessoa × agente», §2).
 *
 * Human Access diz o que a **pessoa** pode: quem pede (claims do JWT) → quais
 * identidades ela alcança → quais tools usa. Identity Access diz o que a
 * **identidade** pode acessar. O acesso efetivo é a **interseção** dos dois,
 * nunca a união: marcar uma identidade em Human Access não concede capability
 * nenhuma à identidade.
 *
 * O item-pai «Controle de acesso» é `ROUTE_ACCESS_CONTROL`: um rótulo do menu, sem tela própria.
 */
export const ROUTE_HUMAN_ACCESS = '/access-control/human';
export const ROUTE_IDENTITY_ACCESS = '/access-control/identities';

/**
 * O endereço do grupo «Controle de acesso». Desde 12/10/2026 ele não tem tela própria: o hub da Fase 2 saiu, e no
 * menu o grupo é só um rótulo (`heading`). O endereço continua valendo para quem o guardou, e redireciona
 * (`routes.tsx`): **com `?identidade=`** é o link antigo de T14 e segue para o Identity Access com a query inteira;
 * **sem ele**, segue para Human Access (T11), o primeiro item do grupo. As telas do console escrevem o endereço
 * de Identity Access direto (`identityAccessHref`).
 */
export const ROUTE_ACCESS_CONTROL = '/access-control';

/** Query param dos links antigos de T14 — ver `ROUTE_ACCESS_CONTROL`. */
export const IDENTITY_PARAM = 'identidade';
/** Fase 2: a aba de Identity Access (T14) que um link abre — `outgoing` (Saída A2A) ou `incoming` (Entrada). */
export const TAB_PARAM = 'tab';

/** O endereço de Identity Access (T14) numa identidade e numa aba. */
export function identityAccessHref(identityId: string, tab?: 'outgoing' | 'incoming'): string {
  const params = new URLSearchParams({ [IDENTITY_PARAM]: identityId });
  if (tab) params.set(TAB_PARAM, tab);
  return `${ROUTE_IDENTITY_ACCESS}?${params.toString()}`;
}

/**
 * O endereço da gaveta de uma identidade (T5) numa aba (aim-ypw): o link da linha «Contrato diferente da declaração
 * da imagem» abre a aba Capabilities. A gaveta lê o `tab` ao abrir e o tira do endereço, para a próxima gaveta não
 * herdar a aba.
 */
export function identityDrawerHref(identityId: string, tab?: 'capabilities'): string {
  const params = new URLSearchParams({ [DRAWER_PARAM]: identityId });
  if (tab) params.set(TAB_PARAM, tab);
  return `/identidades?${params.toString()}`;
}

/**
 * O endereço que T11 teve até 03/09. Continua registrado, redirecionando **com a
 * query string** para a rota nova.
 *
 * Preservar a query não é detalhe: o servidor escreve `/politicas?drawer=<id>`
 * na busca global (`shell_repo`), na auditoria (`audit/links.go`) e no mapa
 * (`accessmap`). Redirect que perde o `?drawer=` levaria à lista em vez da regra.
 */
export const ROUTE_STALE_POLICIES = '/politicas';

/** Query param que abre um drawer na própria rota (`?drawer=<id>`). */
export const DRAWER_PARAM = 'drawer';

export const ROUTES: readonly RouteConsole[] = [
  // ---------------------------------------------------------------- Operação
  {
    href: '/',
    key: 'overview',
    icon: 'gauge',
    group: 'operation',
    tela: 'T3',
  },
  {
    href: '/mapa',
    key: 'accessMap',
    icon: 'table',
    group: 'operation',
    tela: 'T19',
  },
  {
    href: '/aprovacoes',
    key: 'approvals',
    icon: 'flag',
    group: 'operation',
    tela: 'T9',
    contador: 'pending_approvals',
  },
  {
    href: '/decisoes',
    key: 'decisions',
    icon: 'list',
    group: 'operation',
    tela: 'T16',
  },
  {
    href: '/simulador',
    key: 'simulator',
    icon: 'play',
    group: 'operation',
    tela: 'T18',
  },

  // ---------------------------------------------------------------- Descoberta
  {
    href: '/identidades',
    key: 'identities',
    icon: 'robot',
    group: 'discovery',
    tela: 'T4',
    contador: 'identities',
    contadorDiscreto: true,
  },
  {
    href: '/resources',
    key: 'resources',
    icon: 'server',
    group: 'discovery',
    tela: 'T6',
    contador: 'resources',
    contadorDiscreto: true,
  },

  // ---------------------------------------------------------------- Controle
  // O rótulo «Controle de acesso» primeiro (sem tela própria desde 12/10/2026), e recuados sob ele os dois eixos na ordem em que a decisão
  // se lê (pessoa, depois identidade), com Security Contracts abrigado sob o
  // eixo identidade. Runtime Status volta ao nível do grupo — não é acesso.
  //
  // O `href` aqui é **literal**, e não a constante, por causa de um leitor de
  // fora: `control-plane/backend/usecases/audit/links_test.go` lê este arquivo com o regex
  // `href:\s*'([^']*)'` para provar que todo link da trilha de auditoria navega
  // para uma rota que existe. Com a constante o parser não enxerga a rota, e a
  // guarda acusa link morto sobre uma rota viva. O par literal/constante não
  // deriva em silêncio: `navigation.test.tsx` afirma
  // `routeByHref(ROUTE_HUMAN_ACCESS)`.
  {
    href: '/access-control',
    key: 'accessControl',
    icon: 'lock',
    group: 'control',
    tela: 'Grupo',
    heading: true,
  },
  {
    href: '/access-control/human',
    key: 'humanAccess',
    icon: 'shield',
    group: 'control',
    tela: 'T11',
    parent: '/access-control',
    contador: 'policies',
    contadorDiscreto: true,
  },
  {
    href: '/access-control/identities',
    key: 'identityAccess',
    icon: 'key',
    group: 'control',
    tela: 'T14',
    parent: '/access-control',
  },
  {
    href: '/contratos',
    key: 'securityContracts',
    icon: 'file-shield',
    group: 'control',
    tela: 'T13',
    parent: '/access-control',
  },
  {
    href: '/runtime',
    key: 'runtimeStatus',
    icon: 'broadcast',
    group: 'control',
    tela: 'T15',
  },
  // Sites (P1) — o cadastro dos CLUSTERES do tenant, no grupo Controle.
  //
  // Aqui, e nao em Configuracoes, porque o site nao e preferencia do tenant: e a
  // ancora do enderecamento (P7). O recurso carrega o site, a identidade carrega o
  // site, e o SPIFFE ID sai do template DO SITE — as tres coisas que este grupo
  // governa. O `href` vai LITERAL, como os vizinhos, porque
  // `control-plane/backend/usecases/audit/links_test.go` le este arquivo com o regex
  // `href:\s*'([^']*)'` para provar que todo link da trilha navega para uma rota
  // que existe; com constante o parser nao enxerga a rota.
  {
    href: '/sites',
    key: 'sites',
    icon: 'server',
    group: 'control',
    tela: 'T-Sites',
  },

  {
    href: '/approval-workflow',
    key: 'approvalWorkflow',
    icon: 'shield',
    group: 'control',
    tela: 'T-ApprovalWorkflow',
  },

  // ---------------------------------------------------------------- Configurações
  {
    href: '/infra',
    key: 'infrastructure',
    icon: 'database',
    group: 'settings',
    tela: 'T20',
  },
  {
    href: '/settings/environments',
    key: 'environments',
    icon: 'gear',
    group: 'settings',
    tela: 'T-Environments',
  },
  {
    href: '/tenant',
    key: 'tenantSettings',
    icon: 'gear',
    group: 'settings',
    tela: 'T21',
  },
  {
    href: '/usuarios',
    key: 'users',
    icon: 'users',
    group: 'settings',
    tela: 'T22',
    // FR-010: viewer e approver não veem o item — o guard de papel também barra a rota.
    papeis: ['admin'],
  },
  {
    href: '/auditoria',
    key: 'audit',
    icon: 'clock',
    group: 'settings',
    tela: 'T23',
  },
];

export function routeByHref(href: string): RouteConsole | undefined {
  return ROUTES.find((rota) => rota.href === href);
}

/**
 * `/login?expirada=1&retorno=<rota de origem>` — FR-007.
 *
 * `expirada` distingue "sua sessão expirou" (o usuário estava dentro) de "entre
 * para continuar" (chegou pela URL); `retorno` leva de volta à tela de origem
 * depois do login.
 */
export function loginRoute(returnFor?: string | null, expired = false): string {
  const params = new URLSearchParams();
  if (expired) params.set('expirada', '1');
  if (returnFor && returnFor !== ROUTE_LOGIN && !returnFor.startsWith(`${ROUTE_LOGIN}?`)) {
    params.set('retorno', returnFor);
  }
  const query = params.toString();
  return query ? `${ROUTE_LOGIN}?${query}` : ROUTE_LOGIN;
}

export interface RailGroupsOptions {
  /**
   * Papel da sessão. `null`/ausente = ainda não sabemos, e o menu **omite** o que
   * é restrito (fail-closed) — ver `temPapel`. A rota em si continua acessível
   * por URL para quem tiver o papel: quem decide é o servidor.
   */
  papel?: Role | null;
  /** Contadores de `getShell` (FR-012). */
  contadores?: Counters | undefined;
}

/**
 * Menu lateral por papel (FR-010, FR-012): grupos na ordem do spec, itens que o
 * papel não pode usar **omitidos** (não desabilitados) e contadores do `getShell`.
 *
 * Fail-closed de propósito (`temPapel(..., false)`): sem saber o papel, Usuários
 * não aparece. É o único item restrito do console, e o FR-010 manda omiti-lo.
 */
export function railGroups({ papel = null, contadores }: RailGroupsOptions = {}): RailGroup[] {
  return GROUPS.map((grupo) => ({
    title: t(`shell.nav.group.${grupo}`),
    items: ROUTES.filter((rota) => rota.group === grupo && hasRole(papel, rota.papeis, false)).map(
      (rota) => ({
        href: rota.href,
        label: navLabel(rota),
        icon: rota.icon,
        ...(rota.parent ? { sub: true } : {}),
        ...(rota.heading ? { heading: true } : {}),
        ...(rota.contador ? { count: contadores?.[rota.contador] ?? 0 } : {}),
        ...(rota.contadorDiscreto ? { quietCount: true } : {}),
      }),
    ),
  })).filter((grupo) => grupo.items.length > 0);
}

export interface DrawerParam {
  /** `?drawer=<id>` da rota atual, ou `null`. */
  id: string | null;
  /** Abre o drawer empilhando no histórico — "voltar" fecha. */
  abrir: (id: string) => void;
  /** Fecha sem deixar a rota com drawer no histórico. */
  fechar: () => void;
}

/**
 * Convenção de drawer do console: o drawer aberto é `?drawer=<id>` **na mesma
 * rota** da tela, nunca uma rota própria. Assim a URL do drawer é compartilhável
 * e o botão "voltar" do navegador fecha o painel.
 */
export function useDrawerParam(): DrawerParam {
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get(DRAWER_PARAM);

  const open = useCallback(
    (novo: string) => {
      const proximos = new URLSearchParams(searchParams);
      proximos.set(DRAWER_PARAM, novo);
      setSearchParams(proximos);
    },
    [searchParams, setSearchParams],
  );

  const close = useCallback(() => {
    const proximos = new URLSearchParams(searchParams);
    proximos.delete(DRAWER_PARAM);
    setSearchParams(proximos, { replace: true });
  }, [searchParams, setSearchParams]);

  return { id, abrir: open, fechar: close };
}
